import { createContext, useCallback, useContext, useEffect, useMemo, type PropsWithChildren } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ADMIN_AUTH_REQUIRED_EVENT, ApiError, httpClient, runWithAdminSessionMutationLock } from '@/services/httpClient'

export type AdminUser = { id: string; name: string; roleName: string; permissions: string[] }
type Principal = { public_id: string; nickname: string; roles: string[]; permissions: string[] }
type SMSCodeResult = { expires_in: number; retry_after: number; verification_code?: string }
type AuthState = {
  user: AdminUser | null
  pending: boolean
  error: Error | null
  retry: () => void
  login: (account: string, credential: string) => Promise<void>
  requestSMSCode: (phone: string) => Promise<SMSCodeResult>
  logout: () => Promise<void>
  can: (permission?: string) => boolean
}

export const adminPrincipalKey = ['admin-principal'] as const
const AuthContext = createContext<AuthState | null>(null)

function asAdmin(principal: Principal): AdminUser {
  if (!principal.permissions.includes('admin.console.access')) throw new ApiError('当前账号没有管理后台访问权限', 403, 'FORBIDDEN')
  return { id: principal.public_id, name: principal.nickname, roleName: principal.roles.includes('platform_super_admin') ? '平台超级管理员' : principal.roles.join('、'), permissions: principal.permissions }
}

export async function loadAdmin(signal?: AbortSignal) {
  try {
    return asAdmin(await httpClient.get<Principal>('/admin/me', signal))
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient()
  const principal = useQuery({ queryKey: adminPrincipalKey, queryFn: ({ signal }) => loadAdmin(signal), staleTime: 30_000, retry: false, refetchOnWindowFocus: true, refetchOnReconnect: true })
  const error = principal.error
  const user = error instanceof ApiError && error.status === 403 ? null : principal.data ?? null
  const pending = principal.isPending && principal.isFetching
  useEffect(() => {
    const requireAuthentication = () => {
      void queryClient.cancelQueries()
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== adminPrincipalKey[0] })
      queryClient.setQueryData(adminPrincipalKey, null)
    }
    window.addEventListener(ADMIN_AUTH_REQUIRED_EVENT, requireAuthentication)
    return () => window.removeEventListener(ADMIN_AUTH_REQUIRED_EVENT, requireAuthentication)
  }, [queryClient])
  const login = useCallback(async (account: string, credential: string) => {
    await runWithAdminSessionMutationLock(() => httpClient.post('/auth/admin/phone/login', { phone: `+86${account}`, code: credential, device_name: '管理端浏览器' }))
    try {
      const next = asAdmin(await httpClient.get<Principal>('/admin/me'))
      await queryClient.cancelQueries()
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== adminPrincipalKey[0] })
      queryClient.setQueryData(adminPrincipalKey, next)
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) await runWithAdminSessionMutationLock(() => httpClient.post('/auth/admin/logout'))
      throw error
    }
  }, [queryClient])
  const requestSMSCode = useCallback((phone: string) => httpClient.post<SMSCodeResult>('/auth/admin/sms-codes', { phone: `+86${phone}` }), [])
  const logout = useCallback(async () => {
    // Cancel reads before logout so a late 401 cannot start a new refresh afterwards.
    await queryClient.cancelQueries()
    await runWithAdminSessionMutationLock(() => httpClient.post('/auth/admin/logout'))
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== adminPrincipalKey[0] })
    queryClient.setQueryData(adminPrincipalKey, null)
  }, [queryClient])
  const retry = useCallback(() => { void queryClient.invalidateQueries({ queryKey: adminPrincipalKey }) }, [queryClient])
  const value = useMemo<AuthState>(() => ({ user, pending, error, retry, login, requestSMSCode, logout, can: (permission) => !permission || Boolean(user?.permissions.includes(permission)) }), [error, login, logout, pending, requestSMSCode, retry, user])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used within AuthProvider')
  return value
}
